import NotFound from "../404";
import React from "react";
import ShareCertification from "@components/ShareCertification";
import get from "lodash/get";
import {
  Box,
  Button,
  DarkMode,
  Flex,
  Heading,
  Img,
  Link,
  Stack,
  Text,
  useTheme,
} from "@chakra-ui/react";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { Footer } from "@components/Footer";
import {
  Link as GatsbyLink,
  GetServerData,
  PageProps,
  graphql,
  useStaticQuery,
} from "gatsby";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { GetCertificationDocument, GetCertificationQuery } from "@graphql";
import { Header } from "@components/Header";
import { ShieldAnimation } from "@components/ShieldAnimation";
import { TaggedLogo } from "@components/TaggedLogo";
import { encode } from "base-64";
import { request } from "graphql-request";
import { useUser } from "@utils";
import { validate } from "uuid";

interface Certification {
  id: string;
  certificationId: string;
  earnedAt: Date;
  owner: {
    id: string;
    fullName: string;
  };
}

interface ServerDataProps {
  certification?: Certification;
}

export const getServerData: GetServerData<GetCertificationQuery> = async ({
  params,
}) => {
  const id = params?.id as string | undefined;
  if (!id || !validate(id)) {
    return {
      status: 404,
      props: {},
    };
  }

  const { certification } = await request<GetCertificationQuery>(
    process.env.GATSBY_API_URL + "/api/graphql",
    GetCertificationDocument,
    { id }
  );

  return {
    props: {
      certification,
    },
  };
};

const Certification: React.FC<
  PageProps<undefined, undefined, undefined, ServerDataProps>
> = ({ serverData, location }) => {
  const { site, allCertification: certifications } =
    useStaticQuery<Queries.CertificationStaticDataQuery>(
      graphql`
        query CertificationStaticData {
          site {
            siteMetadata {
              siteUrl
            }
          }
          allCertification {
            nodes {
              id
              slug
              progressId
              title
              description
              criteria
              image
              gradient
            }
          }
        }
      `
    );

  const theme = useTheme();

  const { user } = useUser();

  if (!serverData.certification) {
    return <NotFound />;
  }

  const {
    id: certId,
    certificationId,
    owner,
    earnedAt,
  } = serverData.certification;
  const certification = certifications.nodes.find(
    (node) => node.progressId === certificationId
  );

  if (!certification) {
    throw new Error("Unknown certification");
  }

  const { title, description, image, gradient } = certification;

  const issued = `Issued on ${new Date(earnedAt).toLocaleDateString()}`;
  const expiry = "This certification does not expire";
  const isOwner = user !== null && user.id === owner?.id;

  const socialImage = [
    "https://res.cloudinary.com/apollographql/image/upload",
    "w_1280,h_669,c_fill,q_auto,f_auto",
    owner &&
      "w_800,c_fit,co_rgb:FFFFFF,g_south_west,x_80,y_352,l_text:Source%20Sans%20Pro_80_semibold:" +
        encodeURIComponent(owner.fullName),
    "w_800,c_fit,co_rgb:FFFFFF,g_north_west,x_80,y_352,l_text:Source%20Sans%20Pro_56_semibold:" +
      encodeURIComponent(title),
    "w_800,c_fit,co_rgb:FFFFFF,g_south_west,x_80,y_128,l_text:Source%20Sans%20Pro_32:" +
      encodeURIComponent(encodeURIComponent(issued)),
    "w_800,c_fit,co_rgb:FFFFFF,g_south_west,x_80,y_80,l_text:Source%20Sans%20Pro_32_italic:" +
      encodeURIComponent(expiry),
    `l_fetch:${encode(image)},w_256,h_256,g_south_east,x_80,y_80,c_fill`,
    "LinkedIn_Post_Simplified_no_description_qeugyw",
  ]
    .filter(Boolean)
    .join("/");

  return (
    <Box>
      <GatsbySeo
        title="Developer certification"
        openGraph={{
          title,
          description,
          images: [{ url: socialImage }],
        }}
        twitter={{
          cardType: "summary_large_image",
        }}
      />
      <Header borderBottomWidth="1px">{title}</Header>
      <Box px={[6, 8, 10, 12]} pt="8" pb="14" maxW="container.xl" mx="auto">
        <Stack
          spacing={{ base: 6, md: 10 }}
          justify="center"
          direction={{ base: "column", md: "row" }}
        >
          <DarkMode>
            <Flex
              p={[6, 8, 10, 12]}
              maxW="4xl"
              rounded="lg"
              shadow="xl"
              color="white"
              pos="relative"
              css={() => ({
                backgroundImage: `linear-gradient(${[
                  "to bottom right",
                  ...gradient.map((color) => get(theme.colors, color)),
                ]})`,
              })}
            >
              <Stack spacing="12" align="flex-start">
                <TaggedLogo tag="Certified" />
                <div>
                  {owner && (
                    <Heading mb="2" size="2xl">
                      {owner.fullName}
                    </Heading>
                  )}
                  <Heading mb="4">
                    <Link
                      as={GatsbyLink}
                      to={"/certifications/" + certification.slug}
                    >
                      {title}
                    </Link>
                  </Heading>
                  <Text fontSize={{ md: "lg" }}>{description}</Text>
                </div>
                <Box fontSize={{ md: "lg" }}>
                  {issued}
                  <br />
                  <em>{expiry}</em>
                </Box>
              </Stack>
              <ShieldAnimation
                as={Img}
                maxTilt={40}
                display={{
                  base: "none",
                  lg: "block",
                }}
                mt="auto"
                ml="12"
                boxSize="148px"
                src={image}
              />
              {!isOwner && (
                <Button
                  display={{ base: "none", md: "flex" }}
                  rightIcon={<FiArrowRight />}
                  pos="absolute"
                  top="6"
                  right="6"
                  variant="link"
                  color="white"
                  size="lg"
                  as={GatsbyLink}
                  to={"/certifications/" + certification.id}
                  _active={{ color: "gray.100" }}
                >
                  Learn about certification
                </Button>
              )}
              <Box
                pos="absolute"
                bottom="3"
                right="3"
                fontSize="xs"
                color="whiteAlpha.900"
              >
                ID: {certId}
              </Box>
            </Flex>
          </DarkMode>
          {isOwner && (
            <Box flexGrow={1}>
              <Heading size="md" mb="6" display={{ base: "none", md: "block" }}>
                Share certification
              </Heading>
              <ShareCertification
                title={title}
                certId={certId}
                certUrl={site?.siteMetadata?.siteUrl + location.pathname}
                certificateProps={{
                  title,
                  owner,
                  description,
                  issued,
                  expiry,
                  image:
                    "https://res.cloudinary.com/apollographql/image/fetch/f_png,w_350/" +
                    image,
                }}
              />
            </Box>
          )}
        </Stack>
      </Box>
      <Footer />
    </Box>
  );
};

export default Certification;
